/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { ArrowDown, ArrowUp, CornerDownLeft, Hash, Search, type LucideIcon } from 'lucide-react';
import { Icon } from '../ui/Icon';
import { searchTargets, type JumpTarget } from './routes';

export interface JumpToProps {
  open: boolean;
  onClose: () => void;
  targets: JumpTarget[];
  /** Pages visited lately, most recent first (shown before anything is typed). */
  recent: JumpTarget[];
  iconFor: (target: JumpTarget) => LucideIcon;
  onPick: (target: JumpTarget) => void;
}

/** The Ctrl K "Jump to" box: type part of a page, section or setting name, arrow to it, Enter opens it. */
export function JumpTo(props: JumpToProps) {
  if (!props.open) return null;
  return <JumpToOpen {...props} />;
}

function JumpToOpen({ onClose, targets, recent, iconFor, onPick }: JumpToProps) {
  const dialog = useRef<HTMLDialogElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const listId = useId();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const close = useRef(onClose);
  useEffect(() => {
    close.current = onClose;
  }, [onClose]);

  const groups = useMemo(() => {
    const found = searchTargets(targets, query, 9);
    if (query.trim()) {
      const pages = found.filter((t) => t.kind === 'page');
      const sections = found.filter((t) => t.kind === 'section');
      return [
        ...(pages.length ? [{ label: 'Pages', items: pages }] : []),
        ...(sections.length ? [{ label: 'Sections', items: sections }] : []),
      ];
    }
    const recentKeys = new Set(recent.map((t) => t.key));
    return [
      ...(recent.length ? [{ label: 'Recent', items: recent }] : []),
      { label: 'Pages', items: targets.filter((t) => t.kind === 'page' && !recentKeys.has(t.key)) },
    ];
  }, [targets, recent, query]);
  const flat = groups.flatMap((group) => group.items);
  const starts = groups.map((_, g) => groups.slice(0, g).reduce((sum, group) => sum + group.items.length, 0));
  const current = Math.min(active, Math.max(0, flat.length - 1));

  useEffect(() => {
    const el = dialog.current;
    if (!el) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (!el.open) el.showModal();
    input.current?.focus();
    const onCancel = (event: Event) => {
      event.preventDefault();
      close.current();
    };
    el.addEventListener('cancel', onCancel);
    return () => {
      el.removeEventListener('cancel', onCancel);
      if (el.open) el.close();
      opener?.focus?.();
    };
  }, []);

  useEffect(() => {
    dialog.current?.querySelector(`[data-index="${current}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [current]);

  const onKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!flat.length) return;
      setActive((current + (event.key === 'ArrowDown' ? 1 : -1) + flat.length) % flat.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const target = flat[current];
      if (target) onPick(target);
    }
  };

  return (
    <dialog
      ref={dialog}
      className="shell-palette"
      aria-label="Jump to"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="shell-palette-box">
        <div className="shell-palette-search">
          <Icon icon={Search} size="lg" />
          <input
            ref={input}
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-activedescendant={flat.length ? `${listId}-${current}` : undefined}
            aria-autocomplete="list"
            aria-label="Jump to a page or section"
            placeholder="Jump to a page, section or setting"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActive(0);
            }}
            onKeyDown={onKey}
            spellCheck={false}
            autoComplete="off"
          />
          <kbd className="ui-kbd">Esc</kbd>
        </div>
        <div className="shell-palette-list" id={listId} role="listbox" aria-label="Matches">
          {flat.length === 0 ? (
            <p className="shell-palette-none">Nothing matches &ldquo;{query.trim()}&rdquo;. Try a page name such as Caps, Support or Governance.</p>
          ) : groups.map((group, g) => (
            <div key={group.label} role="group" aria-label={group.label}>
              <p className="shell-palette-group" aria-hidden="true">{group.label}</p>
              {group.items.map((target, i) => {
                const mine = (starts[g] ?? 0) + i;
                const on = mine === current;
                return (
                  <div
                    key={`${group.label}-${target.key}`}
                    id={`${listId}-${mine}`}
                    data-index={mine}
                    role="option"
                    aria-selected={on}
                    className={`shell-palette-item${on ? ' is-active' : ''}`}
                    onMouseMove={() => { if (!on) setActive(mine); }}
                    onClick={() => onPick(target)}
                  >
                    <span className="shell-palette-icon"><Icon icon={target.kind === 'section' ? Hash : iconFor(target)} size="md" /></span>
                    <span className="shell-palette-label">{target.label}</span>
                    <span className="shell-palette-where">{target.where}</span>
                    {on ? <kbd className="ui-kbd shell-palette-enter">Enter</kbd> : null}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div className="shell-palette-foot" aria-hidden="true">
          <kbd className="ui-kbd"><Icon icon={ArrowUp} size="xs" /></kbd>
          <kbd className="ui-kbd"><Icon icon={ArrowDown} size="xs" /></kbd>
          <span>to move</span>
          <kbd className="ui-kbd"><Icon icon={CornerDownLeft} size="xs" /></kbd>
          <span>to open</span>
          <span className="shell-palette-foot-note">Pages, sections and settings</span>
        </div>
      </div>
    </dialog>
  );
}
